import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useWindowDimensions } from 'react-native';

import Avatar, { AvatarProps } from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { MemberListSummaryV3 } from '@components/MemberListSummary/MemberListSummaryV3';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import {
  TaskInviteOrAssignee,
  UserSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TaskMemberRole } from '@graphql/generated';
import {
  shouldShowPaywallForProject,
  ProjectPaywallShape,
} from '@utils/teams/paywall';

interface AssigneeMembersProps {
  selectedUser?: UserSearchType[];
  onAssignMember: (members: UserSearchType[]) => void;
  onSetShowInviteOrAssignModal: (show: boolean) => void;
  disabled?: boolean;
  doNotShrink?: boolean;
  taskId?: string;
  project?: ProjectPaywallShape | null;
  maxMemberCount?: number;
  isTaskPanel?: boolean;
  accessibilityLabel?: string;
  isProjectEditor?: boolean;
  isTeamMember?: boolean;
  initialOpen?: boolean;
}

export const TaskAssigneeMembersButtonV3: React.FC<AssigneeMembersProps> = ({
  onAssignMember,
  selectedUser = [],
  onSetShowInviteOrAssignModal,
  disabled,
  doNotShrink = false,
  taskId,
  project,
  maxMemberCount = 3,
  isTaskPanel = false,
  accessibilityLabel,
  isProjectEditor = false,
  isTeamMember = false,
  initialOpen = false,
}) => {
  const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();

  // For checking whether single assignee name is visually truncated (web)
  const textAssigneeRef = useRef<any>(null);
  const [isAssigneeTruncated, setIsAssigneeTruncated] = useState(false);
  const [showInviteOrAssignModal, setShowInviteOrAssignModal] = useState(false);

  useEffect(() => {
    if (initialOpen) {
      setShowInviteOrAssignModal(true);
    }
  }, [initialOpen]);

  const assigneeList = selectedUser.filter(
    (t) => t.role === TaskMemberRole.Assignee
  );

  const { width: screenWidth } = useWindowDimensions();

  const [displayButtonTitle, setDisplayButtonTitle] = useState(
    doNotShrink ? true : screenWidth > 1280
  );

  // Control whether the button shows "Assignee" text or only icon
  useEffect(() => {
    if (doNotShrink) return;
    setDisplayButtonTitle(screenWidth > 1280);
  }, [screenWidth, doNotShrink]);

  // Let parent know whether modal is visible
  useEffect(() => {
    onSetShowInviteOrAssignModal(showInviteOrAssignModal);
  }, [showInviteOrAssignModal, onSetShowInviteOrAssignModal]);

  // Helper to recompute truncation for the single assignee name
  const recalcTruncation = useCallback(() => {
    if (assigneeList.length === 1 && textAssigneeRef.current) {
      const el = textAssigneeRef.current;
      // scrollWidth > clientWidth means the text is visually truncated
      setIsAssigneeTruncated(el.scrollWidth > el.clientWidth);
    } else {
      setIsAssigneeTruncated(false);
    }
  }, [assigneeList.length]);

  // Run truncation check whenever assignee list changes
  useEffect(() => {
    recalcTruncation();
  }, [assigneeList, recalcTruncation]);

  const userAvatars = (members: UserSearchType[]): AvatarProps[] =>
    members.map((u) => {
      if (u.__typename === 'User') {
        const { name, id, avatar } = u;
        return {
          id,
          label: name,
          avatar,
          size: 'memberList',
        };
      } else {
        const { title, id } = u;
        return {
          id: id,
          label: title,
          accessibilityLabel: 'TaskInvitation',
          avatar: undefined,
          size: 'memberList',
        };
      }
    });

  const renderModal = () => {
    if (!showInviteOrAssignModal) return null;

    return (
      <WebModalV3
        canOverflowModal={true}
        accessibilityLabel='Assign Task Member'
        onClose={() => {
          setShowInviteOrAssignModal(false);
        }}
        visible={showInviteOrAssignModal}
        width={800}
        height={412}>
        <TaskInviteOrAssignee
          isProjectEditor={isProjectEditor}
          isTeamMember={isTeamMember}
          selectedUser={selectedUser}
          onAssignMember={onAssignMember}
          taskIdList={taskId ? [taskId] : []}
          onClose={() => {
            setShowInviteOrAssignModal(false);
          }}
        />
      </WebModalV3>
    );
  };

  const showPaywall = project && shouldShowPaywallForProject(project);

  // Case 1: 0 or 1 assignee -> button with placeholder or single user
  if (assigneeList.length < 2) {
    return (
      <>
        {renderModal()}
        <UpgradePaywallTooltip show={showPaywall ?? false}>
          <BaseButtonV3
            accessibilityLabel={accessibilityLabel}
            disabled={disabled}
            mr='m'
            onPress={() => {
              if (!disabled) {
                if (project && shouldShowPaywallForProject(project)) {
                  setPaywallTeam(project?.team ?? undefined);
                  setPaywallModalOpen(true);
                  return;
                }
                setShowInviteOrAssignModal(true);
              }
            }}>
            <Box width={displayButtonTitle ? 116 : 32} alignItems='center'>
              {/* No assignee -> "Assignee" with plus icon */}
              {assigneeList.length === 0 && (
                <Box flexDirection='row' alignItems='center'>
                  <Icon
                    name='UserPlus'
                    color={disabled ? 'grey04' : 'textSecondary'}
                    variant={isTaskPanel ? 's' : 'xxs'}
                  />
                  {displayButtonTitle && (
                    <Text
                      flexShrink={1}
                      ml='xxs'
                      ellipsizeMode='tail'
                      variant={
                        isTaskPanel ? 'webBodySecondary' : 'webBodyHintSubline'
                      }
                      color={disabled ? 'grey04' : 'textSecondary'}
                      numberOfLines={1}>
                      Assignee
                    </Text>
                  )}
                </Box>
              )}

              {/* One assignee -> avatar + name, tooltip only when truncated */}
              {assigneeList.length === 1 && (
                <TooltipWebV3
                  marginOffset={8}
                  disableHoverListener={!isAssigneeTruncated}
                  disableFocusListener={!isAssigneeTruncated}
                  disableTouchListener={!isAssigneeTruncated}
                  title={
                    <Box flexDirection='row' alignItems='center'>
                      {assigneeList[0].__typename === 'User' ? (
                        <Avatar
                          id={assigneeList[0].id}
                          width={16}
                          size='xs'
                          height={16}
                          label={assigneeList[0].name}
                          avatar={assigneeList[0].avatar}></Avatar>
                      ) : (
                        <EmailAvatar width={16} icon='User' />
                      )}
                      <Text
                        ml='xxs'
                        accessibilityLabel='User Name'
                        variant='labelSmall'
                        color='white'
                        style={{ letterSpacing: 0.02 }}>
                        {assigneeList[0].__typename === 'User'
                          ? assigneeList[0].name
                          : assigneeList[0].title}
                      </Text>
                    </Box>
                  }
                  placement='bottom-end'>
                  <Box
                    width={100}
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='flex-start'>
                    {assigneeList[0].__typename === 'User' ? (
                      <Avatar
                        id={assigneeList[0].id}
                        width={20}
                        size='xs'
                        height={20}
                        label={assigneeList[0].name}
                        avatar={assigneeList[0].avatar}
                      />
                    ) : (
                      <EmailAvatar width={16} icon='User' />
                    )}
                    <Text
                      ref={textAssigneeRef}
                      width={75}
                      ml='xxs'
                      flex={1}
                      flexShrink={1}
                      ellipsizeMode='tail'
                      accessibilityLabel='Task Assignee'
                      variant='metadata'
                      color='textSecondary'
                      style={{ letterSpacing: 0.02 }}
                      numberOfLines={1}
                      // If layout changes (resize), recompute truncation
                      onLayout={recalcTruncation}>
                      {assigneeList[0].__typename === 'User'
                        ? assigneeList[0].name
                        : assigneeList[0].title}
                    </Text>
                  </Box>
                </TooltipWebV3>
              )}
            </Box>
          </BaseButtonV3>
        </UpgradePaywallTooltip>
      </>
    );
  }

  // Case 2: 2 or more assignees -> stacked avatar summary
  return (
    <>
      {renderModal()}
      <UpgradePaywallTooltip show={showPaywall ?? false}>
        <Box
          mr='m'
          width={116}
          ml='xxxs'
          pl='xxs'
          style={{
            alignItems: 'flex-start',
          }}>
          <MemberListSummaryV3
            showTooltip={true}
            marginLeft={-4}
            avatars={userAvatars(assigneeList)}
            onPress={() => {
              if (!disabled) {
                if (project && shouldShowPaywallForProject(project)) {
                  setPaywallTeam(project?.team ?? undefined);
                  setPaywallModalOpen(true);
                  return;
                }
                setShowInviteOrAssignModal(true);
              }
            }}
            maxAvatars={maxMemberCount}
          />
        </Box>
      </UpgradePaywallTooltip>
    </>
  );
};
